import { CopyUtil, Core, GenericModal, MessagesModule } from '@everfi/reactifi';
import { getCustomizedText } from 'lib/utility/helpers';
import i18n from 'lib/utility/i18n';
import { kebabCase } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { Tab, Tabs } from 'react-bootstrap';
import { getLocaleVersion } from '../../../common/functions';
import Header from './Header';
import Queue from './Queue';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { sanitizeUrl } from '@braintree/sanitize-url';

export default class Playlist extends React.Component {
  static propTypes = {
    awardSharingPlaylist: PropTypes.func,
    diagnostic: PropTypes.object,
    handleContentNav: PropTypes.func.isRequired,
    handleIncentiveCompleteGateways: PropTypes.func.isRequired,
    loadUserPlaylist: PropTypes.func,
    getUserPlaylists: PropTypes.func,
    program: PropTypes.shape({
      remove_content_images: PropTypes.bool,
      text_customization: PropTypes.shape({
        activities_text: PropTypes.string,
      }),
      id: PropTypes.string,
    }).isRequired,
    playlist: PropTypes.shape({
      content: PropTypes.array,
      id: PropTypes.string,
      incentive: PropTypes.object,
      startedCount: PropTypes.number,
      completedCount: PropTypes.number,
      social_media_post_raw: PropTypes.string,
      completed_course_message: PropTypes.string,
    }),
    setDiagnosticStatus: PropTypes.func.isRequired,
    setReminder: PropTypes.func,
    certificateServiceUrl: PropTypes.string,
    playlistCertificateEnabled: PropTypes.bool,
    jwt: PropTypes.string,
    foundryId: PropTypes.string,
    successMessage: PropTypes.string,
    errorMessage: PropTypes.string,
    clearMessages: PropTypes.func,
    bypassRecaptcha: PropTypes.bool,
    facebookAppId: PropTypes.string,
  };

  constructor(props) {
    super(props);
    this.state = {
      activePage: 1,
      completedItems: false,
      activeKey: 0,
      isShareModalOpen: false,
      isPlaylistShared: false,
      isReminderModalOpen: false,
      isSubmittingReminder: false,
      downloadUrl: '',
      reCaptchaVerified: false,
    };
  }

  async componentDidMount() {
    const userPlaylist = await this.props.loadUserPlaylist(this.props.playlist?.id);
    let userAwards = [];
    if (this.props.playlistCertificateEnabled && this.props.foundryId) {
      userAwards = await this.props.getUserPlaylists(
        this.props.certificateServiceUrl,
        this.props.foundryId,
        this.props.playlist?.id,
        this.props.jwt
      );
    }
    if (userAwards?.length) {
      const playlistCompleted = userAwards?.find(
        ({ attributes }) => attributes?.is_granted && attributes?.award_type === 'Certificate'
      );
      this.setState({
        downloadUrl: playlistCompleted?.attributes?.download_url,
      });
    }
    this.setState({ isPlaylistShared: !!userPlaylist?.is_shared });
  }

  retakeSurvey = () => {
    this.props.setDiagnosticStatus('pending');
  };

  handleTabChange = key => {
    if (Number(key) === 1) {
      this.setState({ activePage: 1, completedItems: true, activeKey: 1 });
    } else {
      this.setState({ activePage: 1, completedItems: false, activeKey: 0 });
    }
  };

  setActivePage = activePage => {
    this.setState({ activePage });
  };

  get playlistName() {
    return getLocaleVersion(this.props.playlist, 'name');
  }

  get playlistLink() {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('utm_source', 'socialshare');
    return sanitizeUrl(`${window.location.origin}${window.location.pathname}?${searchParams.toString()}`);
  }

  toggleShareModal = async () => {
    if (!this.state.isPlaylistShared) {
      await this.props.awardSharingPlaylist(this.props.playlist.id);
      this.setState({ isPlaylistShared: true });
    }

    this.setState(prev => ({ isShareModalOpen: !prev.isShareModalOpen }));
  };

  onVerifyCaptcha = () => {
    this.setState({ reCaptchaVerified: true });
  };

  setReminder = async fields => {
    if (!fields['reminder-days'] || !fields['reminder-email'] || this.state.isSubmittingReminder) {
      return;
    }
    this.setState({ isSubmittingReminder: true });
    // clear any lingering message (e.g. an unread toast from a prior submission)
    // before dispatching a fresh one, to avoid a stale/duplicate toast render
    this.props.clearMessages();
    try {
      await this.props.setReminder(
        this.props.playlist.id,
        this.props.program.id,
        fields['reminder-days'],
        fields['reminder-email']
      );
      this.setState({ isReminderModalOpen: false });
    } finally {
      this.setState({ isSubmittingReminder: false });
    }
  };

  toggleReminderModal = () => {
    this.setState(prev => ({
      isReminderModalOpen: !prev.isReminderModalOpen,
    }));
  };

  shareX = () => {
    const text = encodeURIComponent(
      this.props.playlist.social_media_post_raw ||
      i18n.t('Empower your community to improve their financial capability')
    );
    const url = `https://x.com/intent/post?text=${text}&url=${encodeURIComponent(this.playlistLink)}`;
    window.open(sanitizeUrl(url), '_blank', 'noopener,noreferrer');
  };

  shareFacebook = () => {
    const appId = this.props.facebookAppId;
    const url = `https://www.facebook.com/dialog/share?app_id=${appId}&href=${encodeURIComponent(this.playlistLink)}&display=popup`;
    window.open(sanitizeUrl(url), '_blank', 'noopener,noreferrer');
  };

  shareLinkedIn = () => {
    const text = encodeURIComponent(
      `${this.props.playlist.social_media_post_raw ||
      i18n.t('Empower your community to improve their financial capability')} ${this.playlistLink}`
    );
    const url = `https://www.linkedin.com/feed/?shareActive=true&text=${text}`;
    window.open(sanitizeUrl(url), '_blank', 'noopener,noreferrer');
  };

  shareEmail = () => {
    const subject = encodeURIComponent(`${i18n.t('Check out this course:')} ${this.playlistName}`);
    const body = encodeURIComponent(
      `${
        this.props.playlist.social_media_post_raw ||
        i18n.t('Empower your community to improve their financial capability')
      }\n\n${this.playlistLink}`
    );
    const emailUrl = `mailto:?subject=${subject}&body=${body}`;
    window.open(sanitizeUrl(emailUrl), '_blank', 'noopener,noreferrer');
  };

  copyPlaylistLink = () => {
    CopyUtil.copyToClipboard(this.playlistLink);
    this.setState({ showCopyCheckmark: true });
    setTimeout(() => {
      this.setState({ showCopyCheckmark: false });
    }, 5000);
  };

  getTabbedContent = processStatus => {
    const { playlist, program, handleContentNav } = this.props;

    return (
      <Queue
        activePage={this.state.activePage}
        completedItems={this.state.completedItems}
        content={playlist.content}
        handleContentNav={handleContentNav}
        handleTabChange={this.handleTabChange}
        onShareLink={this.toggleShareModal}
        playlistName={this.playlistName}
        processStatus={processStatus}
        setActivePage={this.setActivePage}
        showIcons={!program.remove_content_images}
        downloadUrl={this.state.downloadUrl}
        completedCourseMessage={playlist.completed_course_message}
      />
    );
  };

  render() {
    const { diagnostic, handleIncentiveCompleteGateways, playlist, program, bypassRecaptcha, setReminder } = this.props;

    if (diagnostic && (!playlist || (playlist && !playlist.content.length))) {
      const activities_text = getCustomizedText(program.text_customization, 'activities_text');
      const activitiesNotFoundMessage = i18n.t('We did not find related {{activities}}. You may want to update your answers.', {
        activities: activities_text,
      });
      return (
        <div className="activities-not-found">
          <h3>{activitiesNotFoundMessage}</h3>
          <button onClick={this.retakeSurvey} className="btn btn-link">
            {i18n.t('Take Survey Again')}
          </button>
        </div>
      );
    } else if (playlist) {
      let progressStatuses = [
        {
          key: 'inprogress',
          title: i18n.t('In Progress'),
          count: playlist.startedCount,
        },
        {
          key: 'completed',
          title: i18n.t('Completed'),
          count: playlist.completedCount,
        },
      ];
      return (
        <React.Fragment>
          <MessagesModule
            successMessage={this.props.successMessage}
            errorMessage={this.props.errorMessage}
            clearMessages={this.props.clearMessages}
          />
          <Header
            diagnostic={diagnostic}
            handleIncentiveCompleteGateways={handleIncentiveCompleteGateways}
            incentive={diagnostic ? diagnostic.incentive : playlist.incentive}
            program={program}
            playlist={playlist}
            retakeSurvey={this.retakeSurvey}
            onShare={this.toggleShareModal}
            onRemind={setReminder ? this.toggleReminderModal : undefined}
          />

          <div className="row playlist-row">
            <div className="container">
              <div className="row playlists clearfix">
                <div className="col-12">
                  <Tabs
                    activeKey={this.state.activeKey}
                    className="row uppercase branded nav-tabs learner-tabs ui-default"
                    data-attribute="playlist-tabs"
                    data-object-type="playlists"
                    data-object-id={playlist.id}
                    id="playlist-tabs"
                    onSelect={k => this.handleTabChange(k)}
                  >
                    {progressStatuses.map((statusObj, index) => {
                      const kebabTabTitle = `${kebabCase(statusObj.title)}-tab`;

                      return (
                        <Tab
                          key={index}
                          data-action={`open-${kebabTabTitle}`}
                          data-attribute={kebabTabTitle}
                          data-object-type="playlists"
                          data-object-id={playlist.id}
                          eventKey={index}
                          title={`${statusObj.title} (${statusObj.count})`}
                        >
                          {this.getTabbedContent(statusObj.title)}
                        </Tab>
                      );
                    })}
                  </Tabs>
                </div>
              </div>
            </div>
          </div>
          <GenericModal
            canClose={true}
            onClose={this.toggleShareModal}
            show={this.state.isShareModalOpen}
            title={i18n.t('Share Pathway')}
          >
            <Core.Typography mb={'sm'}>
              {playlist.social_media_post_raw ||
                i18n.t(
                  'Share the gift of knowledge! 🌟 Share the {{ name }} course with your friends and help them start their financial journey too!',
                  { name: this.playlistName }
                )}
            </Core.Typography>
            <Core.Layout id="share-playlist-modal" variant="flex" justifyContent="center" alignItems="center" gap="sm">
              <Core.IconButton
                variant="elevated"
                isRound={true}
                icon={{ iconName: 'x-twitter', prefix: 'fab' }}
                onClick={this.shareX}
                tooltip={{ content: i18n.t('Share on X') }}
                data-action="share-playlist-x-btn"
              />
              <Core.IconButton
                variant="elevated"
                isRound={true}
                icon={{ iconName: 'facebook', prefix: 'fab' }}
                onClick={this.shareFacebook}
                tooltip={{ content: i18n.t('Share on Facebook') }}
                data-action="share-playlist-facebook-btn"
              />
              <Core.IconButton
                variant="elevated"
                isRound={true}
                icon={{ iconName: 'linkedin', prefix: 'fab' }}
                onClick={this.shareLinkedIn}
                tooltip={{ content: i18n.t('Share on LinkedIn') }}
                data-action="share-playlist-linkedin-btn"
              />
              <Core.IconButton
                variant="elevated"
                isRound={true}
                icon={{ iconName: 'envelope', prefix: 'fas' }}
                onClick={this.shareEmail}
                tooltip={{ content: i18n.t('Share via email') }}
                data-action="share-playlist-email-btn"
              />
              <Core.IconButton
                variant="elevated"
                isRound={true}
                icon={{
                  iconName: this.state.showCopyCheckmark ? 'check' : 'link',
                  prefix: this.state.showCopyCheckmark ? 'fas' : 'far',
                }}
                onClick={this.copyPlaylistLink}
                data-action="copy-playlist-link-btn"
                tooltip={{ content: this.state.showCopyCheckmark ? i18n.t('Copied!') : i18n.t('Copy link') }}
              />
            </Core.Layout>
          </GenericModal>
          <GenericModal
            canClose={true}
            onClose={this.toggleReminderModal}
            show={this.state.isReminderModalOpen}
            title={i18n.t('Remind me')}
          >
            <Core.Typography mb={'sm'}>
              {i18n.t('Set yourself a reminder to explore the {{ name }} course at a later time.', {
                name: this.playlistName,
              })}
            </Core.Typography>
            <Core.Form
              fields={[
                {
                  type: 'select',
                  label: i18n.t('Remind me…'),
                  name: 'reminder-days',
                  value: '1',
                  options: [
                    {
                      value: '1',
                      id: '1-day',
                      text: i18n.t('Tomorrow'),
                    },
                    {
                      value: '3',
                      id: '3-days',
                      text: i18n.t('In 3 days'),
                    },
                    { value: '7', id: '7-days', text: i18n.t('Next week') },
                  ],
                },
                {
                  type: 'email',
                  name: 'reminder-email',
                  label: i18n.t('Email'),
                  placeholder: i18n.t('Enter your email'),
                  validators: [
                    {
                      name: 'isEmail',
                      value: true,
                      customErrorMessage: i18n.t('Enter a valid email address'),
                    },
                  ],

                  required: true,
                },
              ]}
              onSubmit={this.setReminder}
              submitDisabled={(!this.state.reCaptchaVerified && !bypassRecaptcha) || this.state.isSubmittingReminder}
              submitButtonText={
                <Core.Label
                  label={{
                    type: 'iconLeft',
                    options: {
                      text: i18n.t('Set Reminder'),
                      icon: {
                        iconName: 'calendar-clock',
                        prefix: 'far',
                      },
                    },
                  }}
                />
              }
            />
            <GoogleReCaptcha onVerify={this.onVerifyCaptcha} />
          </GenericModal>
        </React.Fragment>
      );
    } else {
      return null;
    }
  }
}
